import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, PhoneForwarded, Plus, RefreshCw, ShieldQuestion, Signal } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import { ConfirmButton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/overlays';
import { formatDateTime, formatPhone, formatRelative } from '@/lib/utils';
import {
  PHONE_LINE_STATUSES,
  PHONE_LINE_STATUS_LABELS,
  REPUTATION_SOURCES,
  REPUTATION_SOURCE_LABELS,
  type PhoneLineStatus,
} from '@shared/enums.js';
import type { PhoneLineSummary } from '@shared/types.js';

/**
 * Admin phone-line inventory.
 *
 * The screen exists to answer one question quickly — "is any of my numbers in
 * trouble" — and then to let the owner do something about it. Warnings sit at
 * the top because that is the reason anyone opens this page; the inventory is
 * underneath; per-line history is a drawer rather than a separate route, so
 * comparing two lines does not mean navigating twice.
 *
 * Nothing here configures a telephony provider or touches a credential.
 */

const STATUS_BADGE: Record<PhoneLineStatus, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  active: 'success',
  warming: 'primary',
  backup: 'default',
  paused: 'default',
  flagged: 'danger',
  retired: 'default',
};

/** Placeholders use a 937 number because that is where the owner starts. */
const EXAMPLE_NUMBER = '(937) 555-0123';

type AgentOption = { id: number; name: string };

export function PhoneLinesPage(): JSX.Element {
  const [includeRetired, setIncludeRetired] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [creating, setCreating] = React.useState(false);

  const lines = useQuery({
    queryKey: ['phone-lines', includeRetired],
    queryFn: () => api.phoneLines(includeRetired),
  });
  const warnings = useQuery({ queryKey: ['phone-line-warnings'], queryFn: api.phoneLineWarnings });
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const agentOptions: AgentOption[] = (users.data?.users ?? [])
    .filter((user) => user.isActive && user.agentId)
    .map((user) => ({ id: user.agentId!, name: user.displayName }));
  const selected = lines.data?.lines.find((l) => l.id === selectedId) ?? null;

  return (
    <Page
      title="Phone lines"
      description="Which numbers you call from, what shape they are in, and what the carriers think of them."
    >
      <div className="space-y-5 p-5 md:p-6">
        <WarningsCard warnings={warnings.data?.warnings ?? []} loading={warnings.isLoading} />
        {lines.data && <NumberPoolSummary lines={lines.data.lines} />}
        <TelnyxProviderCard />

        <Card>
          <CardHeader>
            <CardTitle>Inventory</CardTitle>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includeRetired}
                  onChange={(e) => setIncludeRetired(e.target.checked)}
                />
                Show retired
              </label>
              <RollupButton />
              <Button size="sm" onClick={() => setCreating((v) => !v)}>
                <Plus className="h-3.5 w-3.5" />
                Add line
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            {creating && <CreateLineForm agentOptions={agentOptions} onDone={() => setCreating(false)} />}

            {lines.isLoading ? (
              <LoadingState />
            ) : !lines.data?.lines.length ? (
              <EmptyState
                title="No phone lines yet"
                description={`Add the number you call from — for example ${EXAMPLE_NUMBER} — and mark it active once it has warmed up.`}
              />
            ) : (
              <InventoryTable
                lines={lines.data.lines}
                selectedId={selectedId}
                onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              />
            )}

            {lines.data && (
              <p className="text-2xs text-muted-foreground">
                LeadForge hands the number to your phone or softphone ({lines.data.provider.kind}). It
                does not place calls itself, so it cannot report exact durations or read carrier call
                records — durations shown anywhere in LeadForge are estimated from when the outcome
                form was open.
              </p>
            )}
          </CardBody>
        </Card>

        {selected && <LineDetail line={selected} allLines={lines.data?.lines ?? []} agentOptions={agentOptions} />}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Warnings
// ---------------------------------------------------------------------------

function WarningsCard({
  warnings,
  loading,
}: {
  warnings: { kind: string; message: string; severity: string }[];
  loading: boolean;
}): JSX.Element | null {
  if (loading) return null;
  if (!warnings.length) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
        <Signal className="h-3.5 w-3.5" />
        No phone-line warnings.
      </div>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Attention</CardTitle>
      </CardHeader>
      <CardBody className="space-y-1.5">
        {warnings.map((warning, index) => (
          <div key={`${warning.kind}-${index}`} className="flex items-start gap-2 text-xs">
            <AlertTriangle
              className={
                warning.severity === 'danger'
                  ? 'mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400'
                  : warning.severity === 'warning'
                    ? 'mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400'
                    : 'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground'
              }
            />
            <span>{warning.message}</span>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function NumberPoolSummary({ lines }: { lines: PhoneLineSummary[] }): JSX.Element {
  const count = (status: PhoneLineStatus) => lines.filter((line) => line.status === status).length;
  const activeAgents = new Set(
    lines.filter((line) => line.status === 'active' && line.defaultAgentId).map((line) => line.defaultAgentId!),
  );
  const backedAgents = new Set(
    lines.filter((line) => line.status === 'backup' && line.defaultAgentId).map((line) => line.defaultAgentId!),
  );
  const uncoveredAgents = [...activeAgents].filter((agentId) => !backedAgents.has(agentId)).length;
  const unassigned = lines.filter((line) => line.status !== 'retired' && !line.defaultAgentId).length;

  const metrics = [
    ['Active', count('active')],
    ['Ready backups', count('backup')],
    ['Warming', count('warming')],
    ['Flagged', count('flagged')],
    ['Paused', count('paused')],
    ['Unassigned', unassigned],
  ] as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Number pool</CardTitle>
        <span className="text-2xs text-muted-foreground">Admin-controlled rotation only</span>
      </CardHeader>
      <CardBody className="space-y-2">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {metrics.map(([label, value]) => (
            <div key={label} className="rounded border border-border bg-muted/20 px-3 py-2">
              <div className="text-lg font-semibold tabular-nums">{value}</div>
              <div className="text-2xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
        <p className={uncoveredAgents > 0 ? 'text-2xs text-amber-600' : 'text-2xs text-muted-foreground'}>
          {activeAgents.size === 0
            ? 'No agent currently has an active pool number.'
            : uncoveredAgents > 0
              ? String(uncoveredAgents) +
                ' active agent' +
                (uncoveredAgents === 1 ? '' : 's') +
                ' still need' +
                (uncoveredAgents === 1 ? 's' : '') +
                ' a ready backup.'
              : 'Every agent with an active number has at least one ready backup.'}
        </p>
      </CardBody>
    </Card>
  );
}

function RollupButton(): JSX.Element {
  const queryClient = useQueryClient();
  const rollup = useMutation({
    mutationFn: () => api.rollupPhoneLineStats({ days: 7 }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['phone-line-stats'] });
      toast.success(`Recomputed ${result.dates.length} days across ${result.linesTouched} lines.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Button
      variant="outline"
      size="sm"
      loading={rollup.isPending}
      onClick={() => rollup.mutate()}
      title="Recompute the last 7 days of per-line statistics. Safe to run as often as you like."
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Recompute stats
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Telnyx provider
// ---------------------------------------------------------------------------

function TelnyxProviderCard(): JSX.Element {
  const queryClient = useQueryClient();
  const [areaCode, setAreaCode] = React.useState('937');

  const status = useQuery({ queryKey: ['telnyx-status'], queryFn: api.telnyxStatus });
  const search = useMutation({
    mutationFn: () => api.searchTelnyxNumbers({ areaCode, limit: 8 }),
    onError: (e: Error) => toast.error(e.message),
  });
  const order = useMutation({
    mutationFn: (phoneNumber: string) => api.orderTelnyxNumber({ phoneNumber }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
      queryClient.invalidateQueries({ queryKey: ['phone-line-warnings'] });
      toast.success(
        result.order.phoneNumber +
          ' purchased and added as a warming line.' +
          (result.identitySetup.success && result.identitySetup.cnam
            ? ' CNAM: ' + result.identitySetup.cnam + '.'
            : ''),
      );
      if (result.identitySetup.error) {
        toast.warning(
          'The number was purchased, but caller identity still needs attention: ' + result.identitySetup.error,
        );
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const telnyx = status.data?.telnyx;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telnyx</CardTitle>
        {telnyx && <Badge variant={telnyx.configured ? 'success' : 'warning'}>{telnyx.configured ? 'Ready' : 'Setup needed'}</Badge>}
      </CardHeader>
      <CardBody className="space-y-3">
        {status.isLoading ? (
          <LoadingState />
        ) : !telnyx?.configured ? (
          <div className="rounded border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Set <span className="font-mono text-foreground">TELNYX_API_KEY</span> on the LeadForge server to enable
            number search, purchase, and reputation checks. Credentials never go in this screen or the database.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-muted-foreground">
              <span>Number purchase: ready</span>
              <span>Number Reputation: {telnyx.account?.reputationAccess?.replace('_', ' ') ?? 'checking'}</span>
              <span>Voice connection: {telnyx.connectionConfigured ? 'configured' : 'not configured'}</span>
              <span>
                Browser JWT: {telnyx.voiceCallingIntegrated ? 'ready' : 'telephony credential needed'}
              </span>
              <span>
                Caller ID:{' '}
                {telnyx.callerNumberConfigured ? 'server default configured' : 'active Telnyx agent line required'}
              </span>
            </div>

            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                search.mutate();
              }}
            >
              <Field label="Area code" hint="Start local. 937 is the default.">
                <Input
                  value={areaCode}
                  inputMode="numeric"
                  maxLength={3}
                  className="w-28"
                  onChange={(event) => setAreaCode(event.target.value.replace(/\D/g, '').slice(0, 3))}
                />
              </Field>
              <Button type="submit" size="sm" loading={search.isPending} disabled={areaCode.length !== 3}>
                Search numbers
              </Button>
            </form>

            {search.data && !search.data.numbers.length && (
              <p className="text-xs text-muted-foreground">No local voice numbers returned for that area code.</p>
            )}

            {!!search.data?.numbers.length && (
              <div className="overflow-x-auto rounded border border-border">
                <table className="data-table text-xs">
                  <thead className="text-left text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 font-medium">Number</th>
                      <th className="px-3 py-2 font-medium">Monthly</th>
                      <th className="px-3 py-2 font-medium">Upfront</th>
                      <th className="px-3 py-2 font-medium">Region</th>
                      <th className="px-3 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {search.data.numbers.map((number) => (
                      <tr key={number.phoneNumber} className="border-b border-border/60 last:border-0">
                        <td className="px-3 py-2 font-mono">{formatPhone(number.phoneNumber.replace('+1', ''))}</td>
                        <td className="px-3 py-2">{number.monthlyCost ? `${number.monthlyCost} ${number.currency ?? ''}` : '—'}</td>
                        <td className="px-3 py-2">{number.upfrontCost ? `${number.upfrontCost} ${number.currency ?? ''}` : '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{number.regions.join(', ') || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <ConfirmButton
                            title={`Purchase ${number.phoneNumber}?`}
                            description="This is a real Telnyx purchase. The number will be added to LeadForge as warming after Telnyx accepts the order."
                            confirmLabel="Purchase number"
                            destructive={false}
                            onConfirm={async () => {
                              await order.mutateAsync(number.phoneNumber);
                            }}
                          >
                            <Button variant="outline" size="sm" disabled={order.isPending}>
                              Purchase
                            </Button>
                          </ConfirmButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

function InventoryTable({
  lines,
  selectedId,
  onSelect,
}: {
  lines: PhoneLineSummary[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="data-table text-xs">
        <thead className="text-left text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-1.5 pr-3 font-medium">Number</th>
            <th className="py-1.5 pr-3 font-medium">Label</th>
            <th className="py-1.5 pr-3 font-medium">Agent</th>
            <th className="py-1.5 pr-3 font-medium">Status</th>
            <th className="py-1.5 pr-3 font-medium">Provider</th>
            <th className="py-1.5 pr-3 font-medium">Today</th>
            <th className="py-1.5 pr-3 font-medium">Cap</th>
            <th className="py-1.5 pr-3 font-medium">Last reputation check</th>
            <th className="py-1.5 font-medium" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr
              key={line.id}
              className={selectedId === line.id ? 'border-b border-border/60 bg-muted/30' : 'border-b border-border/60'}
            >
              <td className="py-1.5 pr-3 font-mono text-2xs">
                {line.e164 ? formatPhone(line.e164.replace('+1', '')) : '—'}
              </td>
              <td className="py-1.5 pr-3">{line.label}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">{line.agentName ?? 'unassigned'}</td>
              <td className="py-1.5 pr-3">
                <Badge variant={STATUS_BADGE[line.status]}>{PHONE_LINE_STATUS_LABELS[line.status]}</Badge>
              </td>
              <td className="py-1.5 pr-3 text-muted-foreground">{line.provider ?? '—'}</td>
              <td className="py-1.5 pr-3 tabular-nums">{line.attemptsToday}</td>
              <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">
                {line.dailyCallCap ?? '—'}
              </td>
              <td className="py-1.5 pr-3 text-muted-foreground">
                {line.latestReputationCheck ? (
                  <span>
                    {formatRelative(line.latestReputationCheck.checkedAt)}
                    {line.latestReputationCheck.labelObserved
                      ? ` — ${line.latestReputationCheck.labelObserved}`
                      : ''}
                  </span>
                ) : (
                  'never'
                )}
              </td>
              <td className="py-1.5">
                <Button variant="outline" size="sm" onClick={() => onSelect(line.id)}>
                  {selectedId === line.id ? 'Close' : 'Manage'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateLineForm({ agentOptions, onDone }: { agentOptions: AgentOption[]; onDone: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState({
    label: '',
    e164: '',
    provider: '',
    providerLineId: '',
    defaultAgentId: '',
    dailyCallCap: '',
    notes: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.createPhoneLine({
        label: form.label,
        e164: form.e164 || null,
        provider: form.provider || null,
        providerLineId: form.providerLineId || null,
        defaultAgentId: form.defaultAgentId ? Number(form.defaultAgentId) : null,
        dailyCallCap: form.dailyCallCap ? Number(form.dailyCallCap) : null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
      queryClient.invalidateQueries({ queryKey: ['phone-line-warnings'] });
      onDone();
      toast.success('Line added, warming up. Assign it to an agent and activate it when it is ready.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form
      className="space-y-2 rounded border border-border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="Label" hint="What you call it. Must be unique.">
          <Input
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Dayton main"
            required
          />
        </Field>
        <Field label="Number" hint="Any US number. Typed however you like.">
          <Input
            value={form.e164}
            onChange={(e) => setForm((f) => ({ ...f, e164: e.target.value }))}
            placeholder={EXAMPLE_NUMBER}
          />
        </Field>
        <Field label="Provider" hint="Who supplies it. A name only — never a credential.">
          <Input
            value={form.provider}
            onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
            placeholder="carrier or reseller"
          />
        </Field>
        <Field label="Provider line ID" hint="Their reference, for a bill or a support ticket.">
          <Input
            value={form.providerLineId}
            onChange={(e) => setForm((f) => ({ ...f, providerLineId: e.target.value }))}
          />
        </Field>
        <Field label="Agent" hint="Who this number belongs to in the pool.">
          <Select
            value={form.defaultAgentId}
            onChange={(e) => setForm((f) => ({ ...f, defaultAgentId: e.target.value }))}
          >
            <option value="">Unassigned</option>
            {agentOptions.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Daily call cap" hint="Warns when reached. Does not block calling.">
          <Input
            type="number"
            min={1}
            value={form.dailyCallCap}
            onChange={(e) => setForm((f) => ({ ...f, dailyCallCap: e.target.value }))}
          />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </Field>
      </div>
      <p className="text-2xs text-muted-foreground">
        New lines start <strong>warming up</strong>. Assign an agent and activate it deliberately — a
        number with no call history behind it burns quickly if it opens on a full queue.
      </p>
      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={create.isPending} disabled={!form.label}>
          Add line
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Per-line detail
// ---------------------------------------------------------------------------

function LineDetail({
  line,
  allLines,
  agentOptions,
}: {
  line: PhoneLineSummary;
  allLines: PhoneLineSummary[];
  agentOptions: AgentOption[];
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {line.label}
          {line.e164 ? ` — ${formatPhone(line.e164.replace('+1', ''))}` : ''}
        </CardTitle>
        <Badge variant={STATUS_BADGE[line.status]}>{PHONE_LINE_STATUS_LABELS[line.status]}</Badge>
      </CardHeader>
      <CardBody>
        <Tabs defaultValue="lifecycle">
          <TabsList className="flex-wrap rounded-xl border border-border bg-muted/40 p-1">
            <TabsTrigger value="lifecycle">Lifecycle</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="reputation">Reputation</TabsTrigger>
            <TabsTrigger value="carrier">Carrier tests</TabsTrigger>
            <TabsTrigger value="stats">Daily volume</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <div className="pt-3">
            <TabsContent value="lifecycle">
              <LifecycleTab line={line} allLines={allLines} />
            </TabsContent>
            <TabsContent value="settings">
              <LineSettingsTab line={line} agentOptions={agentOptions} />
            </TabsContent>
            <TabsContent value="reputation">
              <ReputationTab line={line} />
            </TabsContent>
            <TabsContent value="carrier">
              <CarrierTestTab line={line} />
            </TabsContent>
            <TabsContent value="stats">
              <StatsTab line={line} />
            </TabsContent>
            <TabsContent value="history">
              <HistoryTab line={line} />
            </TabsContent>
          </div>
        </Tabs>
      </CardBody>
    </Card>
  );
}

function useLineMutation(lineId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
    queryClient.invalidateQueries({ queryKey: ['phone-line-warnings'] });
    queryClient.invalidateQueries({ queryKey: ['phone-line-events', lineId] });
  };
}

function LifecycleTab({
  line,
  allLines,
}: {
  line: PhoneLineSummary;
  allLines: PhoneLineSummary[];
}): JSX.Element {
  const invalidate = useLineMutation(line.id);
  const [reason, setReason] = React.useState('');
  const [backupId, setBackupId] = React.useState<string>('');

  const backups = allLines.filter(
    (l) => l.status === 'backup' && l.defaultAgentId && l.defaultAgentId === line.defaultAgentId,
  );

  const setStatus = useMutation({
    mutationFn: (status: PhoneLineStatus) => api.setPhoneLineStatus(line.id, status, reason || null),
    onSuccess: (_, status) => {
      invalidate();
      setReason('');
      toast.success(`${line.label} is now ${PHONE_LINE_STATUS_LABELS[status].toLowerCase()}.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rotate = useMutation({
    mutationFn: () => api.rotatePhoneLine(line.id, Number(backupId), reason || null),
    onSuccess: (result) => {
      invalidate();
      setReason('');
      setBackupId('');
      toast.success(
        `${result.promotedLine.label} is now active; ${result.pausedLine.label} is paused.`,
        { duration: 8000 },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const flagRotate = useMutation({
    mutationFn: () => api.flagAndRotatePhoneLine(line.id, Number(backupId), reason),
    onSuccess: (result) => {
      invalidate();
      setReason('');
      setBackupId('');
      toast.success(
        result.promotedLine.label + ' is now active; ' + result.flaggedLine.label + ' is flagged and quarantined.',
        { duration: 8000 },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (line.status === 'retired') {
    return (
      <p className="text-xs text-muted-foreground">
        This line is retired, and retirement is final — everything recorded against it describes a
        specific period of its life, and bringing it back would make that history ambiguous. If it was
        retired by mistake, add a new line for the number and note the correction.
      </p>
    );
  }

  return (
    <div className="max-w-2xl space-y-3">
      <Field label="Reason" hint="Recorded against every change below. Worth a sentence.">
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Answer rate dropped; T-Mobile shows Scam Likely"
        />
      </Field>

      <div className="flex flex-wrap gap-1.5">
        {PHONE_LINE_STATUSES.filter((s) => s !== line.status && s !== 'retired').map((status) => (
          <Button
            key={status}
            variant="outline"
            size="sm"
            disabled={setStatus.isPending}
            onClick={() => setStatus.mutate(status)}
          >
            {PHONE_LINE_STATUS_LABELS[status]}
          </Button>
        ))}
        <ConfirmButton
          title={`Retire ${line.label}?`}
          description="Retirement is permanent. The line keeps its history but can never be active, backup, warming or paused again."
          confirmLabel="Retire permanently"
          onConfirm={() => setStatus.mutate('retired')}
        >
          <Button variant="outline" size="sm">
            Retire
          </Button>
        </ConfirmButton>
      </div>

      {line.status === 'active' && (
        <div className="space-y-2 rounded border border-border p-3">
          <p className="text-xs font-medium">Rotate to a backup</p>
          <p className="text-2xs text-muted-foreground">
            Pauses this line and promotes the chosen backup, in one step. Both must belong to the same
            agent. Rotation discards whatever reputation this number had built, so it is never
            automatic.
          </p>
          {backups.length === 0 ? (
            <p className="text-2xs text-muted-foreground">
              No backup line for {line.agentName ?? 'this agent'}. Add one and set it to Backup first.
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Promote" className="min-w-[14rem]">
                <Select value={backupId} onChange={(e) => setBackupId(e.target.value)}>
                  <option value="">— choose a backup —</option>
                  {backups.map((backup) => (
                    <option key={backup.id} value={backup.id}>
                      {backup.label}
                      {backup.e164 ? ` (${formatPhone(backup.e164.replace('+1', ''))})` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <ConfirmButton
                title="Rotate now?"
                description="This line is paused and the backup takes over immediately. Both changes are recorded."
                confirmLabel="Rotate"
                onConfirm={() => rotate.mutate()}
              >
                <Button size="sm" disabled={!backupId || rotate.isPending}>
                  <PhoneForwarded className="h-3.5 w-3.5" />
                  Rotate
                </Button>
              </ConfirmButton>
              <ConfirmButton
                title="Flag this number and rotate?"
                description="Use this when carrier or reputation evidence confirms the current number is bad. The current number becomes Flagged and the backup takes over atomically."
                confirmLabel="Flag & rotate"
                onConfirm={() => flagRotate.mutate()}
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!backupId || !reason.trim() || flagRotate.isPending}
                  title={!reason.trim() ? 'Add the evidence/reason first.' : undefined}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Flag & rotate
                </Button>
              </ConfirmButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LineSettingsTab({ line, agentOptions }: { line: PhoneLineSummary; agentOptions: AgentOption[] }): JSX.Element {
  const invalidate = useLineMutation(line.id);
  const [form, setForm] = React.useState({
    label: line.label,
    e164: line.e164 ?? '',
    provider: line.provider ?? '',
    providerLineId: line.providerLineId ?? '',
    defaultAgentId: line.defaultAgentId?.toString() ?? '',
    dailyCallCap: line.dailyCallCap?.toString() ?? '',
    notes: line.notes ?? '',
  });

  React.useEffect(() => {
    setForm({
      label: line.label,
      e164: line.e164 ?? '',
      provider: line.provider ?? '',
      providerLineId: line.providerLineId ?? '',
      defaultAgentId: line.defaultAgentId?.toString() ?? '',
      dailyCallCap: line.dailyCallCap?.toString() ?? '',
      notes: line.notes ?? '',
    });
  }, [line]);

  const effectiveAgentOptions =
    line.defaultAgentId && !agentOptions.some((agent) => agent.id === line.defaultAgentId)
      ? [{ id: line.defaultAgentId, name: line.agentName ?? 'Current agent' }, ...agentOptions]
      : agentOptions;

  const save = useMutation({
    mutationFn: () =>
      api.updatePhoneLine(line.id, {
        label: form.label,
        e164: form.e164 || null,
        provider: form.provider || null,
        providerLineId: form.providerLineId || null,
        defaultAgentId: form.defaultAgentId ? Number(form.defaultAgentId) : null,
        dailyCallCap: form.dailyCallCap ? Number(form.dailyCallCap) : null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="max-w-2xl space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Label">
          <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
        </Field>
        <Field label="Number">
          <Input
            value={form.e164}
            onChange={(e) => setForm((f) => ({ ...f, e164: e.target.value }))}
            placeholder={EXAMPLE_NUMBER}
          />
        </Field>
        <Field label="Agent" hint="Pool owner. An active line cannot be unassigned.">
          <Select
            value={form.defaultAgentId}
            onChange={(e) => setForm((f) => ({ ...f, defaultAgentId: e.target.value }))}
          >
            <option value="" disabled={line.status === 'active'}>
              Unassigned
            </option>
            {effectiveAgentOptions.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Provider" hint="A name. Credentials belong in environment variables, never here.">
          <Input value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} />
        </Field>
        <Field label="Provider line ID">
          <Input
            value={form.providerLineId}
            onChange={(e) => setForm((f) => ({ ...f, providerLineId: e.target.value }))}
          />
        </Field>
        <Field label="Daily call cap" hint="Raises a warning when reached; never blocks a call.">
          <Input
            type="number"
            min={1}
            value={form.dailyCallCap}
            onChange={(e) => setForm((f) => ({ ...f, dailyCallCap: e.target.value }))}
          />
        </Field>
        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </Field>
      </div>
      <p className="text-2xs text-muted-foreground">
        Status is not editable here. It moves through the Lifecycle tab, which records who changed it
        and why.
      </p>
      <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
        Save
      </Button>
    </div>
  );
}

function ReputationTab({ line }: { line: PhoneLineSummary }): JSX.Element {
  const queryClient = useQueryClient();
  const checks = useQuery({
    queryKey: ['phone-line-reputation', line.id],
    queryFn: () => api.reputationChecks(line.id),
  });
  const [form, setForm] = React.useState({ source: 'hiya', labelObserved: '', notes: '' });
  const identity = useQuery({
    queryKey: ['phone-line-provider-identity', line.id],
    queryFn: () => api.telnyxCallerIdentity(line.id),
    enabled: line.provider?.toLowerCase() === 'telnyx' && !!line.providerLineId,
  });
  const [cnam, setCnam] = React.useState('');
  React.useEffect(() => {
    if (identity.data?.identity.cnam) setCnam(identity.data.identity.cnam);
  }, [identity.data?.identity.cnam]);


  const record = useMutation({
    mutationFn: () =>
      api.recordReputationCheck(line.id, {
        source: form.source,
        method: 'manual',
        labelObserved: form.labelObserved || null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-line-reputation', line.id] });
      queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
      queryClient.invalidateQueries({ queryKey: ['phone-line-warnings'] });
      setForm({ source: form.source, labelObserved: '', notes: '' });
      toast.success('Reputation check recorded');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveCnam = useMutation({
    mutationFn: () => api.updateTelnyxCnam(line.id, cnam),
    onSuccess: (result) => {
      queryClient.setQueryData(['phone-line-provider-identity', line.id], result);
      setCnam(result.identity.cnam ?? '');
      toast.success('Outbound CNAM updated. Carrier databases can take time to propagate.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const telnyxCheck = useMutation({
    mutationFn: () => api.refreshTelnyxReputation(line.id, false),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['phone-line-reputation', line.id] });
      queryClient.invalidateQueries({ queryKey: ['phone-lines'] });
      queryClient.invalidateQueries({ queryKey: ['phone-line-warnings'] });
      toast.success(`Telnyx reputation: ${result.reputation.spamRisk ?? 'unknown'}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-3">
      {line.provider?.toLowerCase() === 'telnyx' && (
        <div className="space-y-3">
          <div className="rounded border border-border bg-muted/20 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-medium">Caller identity</p>
                <p className="text-2xs text-muted-foreground">
                  Outbound CNAM is the business name registered for this Telnyx number.
                </p>
              </div>
              <Badge variant={identity.data?.identity.cnamListingEnabled ? 'success' : 'warning'}>
                {identity.data?.identity.cnamListingEnabled ? 'CNAM enabled' : 'CNAM not enabled'}
              </Badge>
            </div>
            {identity.isLoading ? (
              <LoadingState />
            ) : identity.isError ? (
              <p className="text-xs text-red-400">{(identity.error as Error).message}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-end gap-2">
                  <Field label="Outbound CNAM" hint="1-15 letters, numbers, or spaces.">
                    <Input
                      value={cnam}
                      maxLength={15}
                      onChange={(event) => setCnam(event.target.value)}
                      className="w-48"
                    />
                  </Field>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={saveCnam.isPending}
                    disabled={!cnam.trim() || cnam.trim() === (identity.data?.identity.cnam ?? '')}
                    onClick={() => saveCnam.mutate()}
                  >
                    Save CNAM
                  </Button>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-muted-foreground">
                  <span>Registered: {identity.data?.identity.cnam ?? 'none'}</span>
                  <span>Purchased: {identity.data?.identity.purchasedAt ? new Date(identity.data.identity.purchasedAt).toLocaleDateString() : 'unknown'}</span>
                  <span>Inbound CNAM lookup: {identity.data?.identity.inboundCallerNameLookupEnabled ? 'on' : 'off (not needed for outbound identity)'}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-muted/20 p-3">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium">Telnyx Number Reputation</p>
                {identity.data?.identity.account && (
                  <Badge
                    variant={
                      identity.data.identity.account.reputationAccess === 'ready'
                        ? 'success'
                        : identity.data.identity.account.reputationAccess === 'blocked'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {identity.data.identity.account.reputationAccess.replace('_', ' ')}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-2xs text-muted-foreground">
                {identity.data?.identity.account.reason ?? 'Provider reputation monitoring is available for this account.'}
              </p>
              {identity.data?.identity.account.reputationAccess === 'blocked' && (
                <p className="mt-1 text-2xs text-amber-600">
                  Until Telnyx Enterprise + LOA access is approved, use carrier tests and Free Caller Registry for spam-label remediation.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href="https://www.freecallerregistry.com/fcr/" target="_blank" rel="noreferrer">
                  Free Caller Registry
                </a>
              </Button>
              <ConfirmButton
                title="Check Telnyx reputation?"
                description="This uses Telnyx Number Reputation. Cached results are preferred; provider billing can apply to fresh reputation data."
                confirmLabel="Run check"
                destructive={false}
                onConfirm={async () => {
                  await telnyxCheck.mutateAsync();
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  loading={telnyxCheck.isPending}
                  disabled={identity.data?.identity.account.reputationAccess !== 'ready'}
                >
                  <ShieldQuestion className="h-3.5 w-3.5" />
                  Check Telnyx
                </Button>
              </ConfirmButton>
            </div>
          </div>
        </div>
      )}

      <form
        className="flex flex-wrap items-end gap-2 rounded border border-border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          record.mutate();
        }}
      >
        <Field label="Source" className="min-w-[12rem]">
          <Select value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
            {REPUTATION_SOURCES.map((source) => (
              <option key={source} value={source}>
                {REPUTATION_SOURCE_LABELS[source]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Label shown" className="min-w-[12rem]" hint="Verbatim: 'Spam Likely', 'clean'.">
          <Input
            value={form.labelObserved}
            onChange={(e) => setForm((f) => ({ ...f, labelObserved: e.target.value }))}
          />
        </Field>
        <Field label="Notes" className="min-w-[14rem] flex-1">
          <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </Field>
        <Button type="submit" size="sm" loading={record.isPending}>
          <ShieldQuestion className="h-3.5 w-3.5" />
          Record check
        </Button>
      </form>

      {checks.isLoading ? (
        <LoadingState />
      ) : !checks.data?.checks.length ? (
        <p className="text-xs text-muted-foreground">
          No reputation checks recorded. Look the number up and note what you see — that record is the
          only way to tell a number going bad from a bad week.
        </p>
      ) : (
        <table className="data-table text-xs">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-1.5 pr-3 font-medium">When</th>
              <th className="py-1.5 pr-3 font-medium">Source</th>
              <th className="py-1.5 pr-3 font-medium">Label</th>
              <th className="py-1.5 pr-3 font-medium">Notes</th>
              <th className="py-1.5 font-medium">By</th>
            </tr>
          </thead>
          <tbody>
            {checks.data.checks.map((check) => (
              <tr key={check.id} className="border-b border-border/60">
                <td className="py-1.5 pr-3 text-muted-foreground">{formatDateTime(check.checkedAt)}</td>
                <td className="py-1.5 pr-3">{REPUTATION_SOURCE_LABELS[check.source]}</td>
                <td className="py-1.5 pr-3">{check.labelObserved ?? '—'}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">{check.notes ?? '—'}</td>
                <td className="py-1.5 text-muted-foreground">{check.checkedByName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CarrierTestTab({ line }: { line: PhoneLineSummary }): JSX.Element {
  const queryClient = useQueryClient();
  const tests = useQuery({
    queryKey: ['phone-line-carrier-tests', line.id],
    queryFn: () => api.carrierTests(line.id),
  });
  const [form, setForm] = React.useState({
    carrier: '',
    destinationLabel: '',
    callerIdDisplay: '',
    spamLabel: '',
    notes: '',
  });

  const record = useMutation({
    mutationFn: () =>
      api.recordCarrierTest(line.id, {
        carrier: form.carrier,
        destinationLabel: form.destinationLabel || null,
        callerIdDisplay: form.callerIdDisplay || null,
        spamLabel: form.spamLabel || null,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-line-carrier-tests', line.id] });
      setForm({ carrier: '', destinationLabel: '', callerIdDisplay: '', spamLabel: '', notes: '' });
      toast.success('Carrier test recorded');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-3">
      <form
        className="space-y-2 rounded border border-border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          record.mutate();
        }}
      >
        <p className="text-2xs text-muted-foreground">
          Ring a real handset and write down what its screen said. A reputation lookup can report a
          number as clean while a carrier prints “Scam Likely” on it.
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          <Field label="Carrier">
            <Input
              value={form.carrier}
              onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))}
              placeholder="Verizon"
              required
            />
          </Field>
          <Field label="Handset" hint="A description, not a number.">
            <Input
              value={form.destinationLabel}
              onChange={(e) => setForm((f) => ({ ...f, destinationLabel: e.target.value }))}
              placeholder="my personal mobile"
            />
          </Field>
          <Field label="Caller ID shown">
            <Input
              value={form.callerIdDisplay}
              onChange={(e) => setForm((f) => ({ ...f, callerIdDisplay: e.target.value }))}
              placeholder="the business name, or the bare number"
            />
          </Field>
          <Field label="Spam label" hint="Blank if the handset printed no warning.">
            <Input
              value={form.spamLabel}
              onChange={(e) => setForm((f) => ({ ...f, spamLabel: e.target.value }))}
              placeholder="Scam Likely"
            />
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Field>
        </div>
        <Button type="submit" size="sm" loading={record.isPending} disabled={!form.carrier}>
          Record test
        </Button>
      </form>

      {tests.isLoading ? (
        <LoadingState />
      ) : !tests.data?.tests.length ? (
        <p className="text-xs text-muted-foreground">No carrier tests recorded for this line.</p>
      ) : (
        <table className="data-table text-xs">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-1.5 pr-3 font-medium">When</th>
              <th className="py-1.5 pr-3 font-medium">Carrier</th>
              <th className="py-1.5 pr-3 font-medium">Handset</th>
              <th className="py-1.5 pr-3 font-medium">Caller ID</th>
              <th className="py-1.5 pr-3 font-medium">Spam label</th>
              <th className="py-1.5 font-medium">By</th>
            </tr>
          </thead>
          <tbody>
            {tests.data.tests.map((test) => (
              <tr key={test.id} className="border-b border-border/60">
                <td className="py-1.5 pr-3 text-muted-foreground">{formatDateTime(test.testedAt)}</td>
                <td className="py-1.5 pr-3">{test.carrier}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">{test.destinationLabel ?? '—'}</td>
                <td className="py-1.5 pr-3">{test.callerIdDisplay ?? '—'}</td>
                <td className="py-1.5 pr-3">
                  {test.spamLabel ? <Badge variant="danger">{test.spamLabel}</Badge> : '—'}
                </td>
                <td className="py-1.5 text-muted-foreground">{test.testedByName ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatsTab({ line }: { line: PhoneLineSummary }): JSX.Element {
  const stats = useQuery({
    queryKey: ['phone-line-stats', line.id],
    queryFn: () => api.phoneLineStats(line.id),
  });

  if (stats.isLoading) return <LoadingState />;
  if (!stats.data?.stats.length) {
    return (
      <p className="text-xs text-muted-foreground">
        No statistics yet. They are computed from call attempts — press “Recompute stats” above after
        a day of calling.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <table className="data-table text-xs">
        <thead className="text-left text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-1.5 pr-3 font-medium">Date</th>
            <th className="py-1.5 pr-3 font-medium">Attempts</th>
            <th className="py-1.5 pr-3 font-medium">Reached someone</th>
            <th className="py-1.5 pr-3 font-medium">Distinct numbers</th>
            <th className="py-1.5 font-medium">Average length</th>
          </tr>
        </thead>
        <tbody>
          {stats.data.stats.map((stat) => (
            <tr key={stat.id} className="border-b border-border/60">
              <td className="py-1.5 pr-3">{stat.statDate}</td>
              <td className="py-1.5 pr-3 tabular-nums">{stat.attempts}</td>
              <td className="py-1.5 pr-3 tabular-nums">{stat.connects}</td>
              <td className="py-1.5 pr-3 tabular-nums">{stat.distinctDestinations}</td>
              <td className="py-1.5 tabular-nums text-muted-foreground">
                {stat.averageDurationSeconds === null
                  ? '—'
                  : `${Math.round(stat.averageDurationSeconds)}s`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-2xs text-muted-foreground">
        “Reached someone” counts an attempt whose outcome means a person was spoken to, or which a
        provider confirmed as answered. Average length is an <strong>estimate</strong>: LeadForge
        hands the call to your phone and never owns the leg, so it can only measure how long the
        outcome form was open.
      </p>
    </div>
  );
}

function HistoryTab({ line }: { line: PhoneLineSummary }): JSX.Element {
  const events = useQuery({
    queryKey: ['phone-line-events', line.id],
    queryFn: () => api.phoneLineEvents(line.id),
  });

  if (events.isLoading) return <LoadingState />;
  return (
    <div className="space-y-2">
      <table className="data-table text-xs">
        <thead className="text-left text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-1.5 pr-3 font-medium">When</th>
            <th className="py-1.5 pr-3 font-medium">Change</th>
            <th className="py-1.5 pr-3 font-medium">Reason</th>
            <th className="py-1.5 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {events.data?.events.map((event) => (
            <tr key={event.id} className="border-b border-border/60">
              <td className="py-1.5 pr-3 text-muted-foreground">{formatDateTime(event.createdAt)}</td>
              <td className="py-1.5 pr-3">
                {event.previousStatus
                  ? `${PHONE_LINE_STATUS_LABELS[event.previousStatus]} → ${PHONE_LINE_STATUS_LABELS[event.newStatus]}`
                  : `Created as ${PHONE_LINE_STATUS_LABELS[event.newStatus]}`}
              </td>
              <td className="py-1.5 pr-3 text-muted-foreground">{event.reason ?? '—'}</td>
              <td className="py-1.5 text-muted-foreground">{event.actorName ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-2xs text-muted-foreground">
        Lifecycle history is append-only — the database refuses to update or delete a row here. A
        mistake is corrected by making another change with a reason, not by editing the record.
      </p>
    </div>
  );
}
